import { cn } from '@repo/ui/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'

// Atom for a heading element with a weight, level, leading, tracking, alignment, tone, and maxLines

export type HeadingProps = {
  as?: React.ElementType
} & React.ComponentPropsWithoutRef<React.ElementType> &
  VariantProps<typeof headingVariants>

const headingVariants = cva('', {
  variants: {
    weight: {
      weak: 'font-medium',
      regular: 'font-semibold',
    },
    level: {
      1: 'text-2xl tracking-tight leading-none',
      2: 'text-xl tracking-tight leading-none',
      3: 'text-lg tracking-tight leading-none',
      4: 'text-base tracking-normal leading-normal',
      5: 'text-sm tracking-normal leading-normal',
    },
    leading: {
      none: 'leading-none',
      tight: 'leading-tight',
      normal: 'leading-normal',
    },
    tracking: {
      tight: 'tracking-tight',
      normal: 'tracking-normal',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    tone: {
      inherit: '',
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      critical: 'text-critical-text-dark',
      success: 'text-success-text-dark',
      warning: 'text-warning-text-dark',
      info: 'text-info-text-dark',
    },
    maxLines: {
      one: 'line-clamp-1 text-ellipsis',
      two: 'line-clamp-2 text-ellipsis',
      three: 'line-clamp-3 text-ellipsis',
    },
  },
  defaultVariants: {
    weight: 'regular',
    level: 1,
    align: 'left',
    tone: 'default',
  },
})

export function Heading({ as, level = 1, ...props }: HeadingProps) {
  const { weight, align, tone, maxLines, className, ...rest } = props

  const safeLevel = Math.max(1, Math.min(5, level || 1))
  const Component = (as || `h${safeLevel}`) as React.ElementType

  return (
    <Component
      className={cn(headingVariants({ weight, level, align, tone, maxLines }), className)}
      {...rest}
    />
  )
}
